// src/components/AccountsReceivable.jsx
import React, { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import { Link, useNavigate } from "react-router-dom";
import "../styles/AccountsReceivable.css";

const UNLOCK_CODE = "1970";

const AccountsReceivable = () => {
  const [clientList, setClientList] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [combined, setCombined] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // 🔒 Estados del candado
  const [summaryUnlocked, setSummaryUnlocked] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const [clientsRes, accountsRes] = await Promise.all([
        api.get("/api/clients"),
        api.get("/api/accountsReceivable"),
      ]);

      setClientList(clientsRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error("Error al obtener datos:", error);

      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    }
  };

  const combineData = useCallback(() => {
    let temp = clientList.map((client) => {
      const matchingAccount = accounts.find((a) => a.clientId === client.id);
      return {
        clientId: client.id,
        name: client.name,
        totalPaid: matchingAccount ? matchingAccount.totalPaid : 0,
        totalPending: matchingAccount ? matchingAccount.totalPending : 0,
      };
    });

    if (searchQuery) {
      temp = temp.filter((item) =>
        (item.name || "").toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setCombined(temp);
  }, [clientList, accounts, searchQuery]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    combineData();
  }, [combineData]);

  const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat("es-CR", {
      style: "currency",
      currency: "CRC",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // 🔓 Validar código
  const handleUnlock = () => {
    if (codeInput.trim() === UNLOCK_CODE) {
      setSummaryUnlocked(true);
      setCodeError("");
      setCodeInput("");
    } else {
      setCodeError("Código incorrecto");
    }
  };

  // 🔒 Volver a bloquear
  const handleLock = () => {
    setSummaryUnlocked(false);
    setCodeInput("");
    setCodeError("");
  };

  // Enter para desbloquear
  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleUnlock();
  };

  return (
    <div className="accounts-receivable">
      <h1>Cuentas por Cobrar</h1>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar cliente..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button type="button">Buscar</button>
      </div>

      <div className="add-client">
        <Link to="/clients">Gestionar Clientes</Link>
      </div>

      <table>
        <thead>
          <tr>
            <th>Cliente</th>
            <th>Total Pendiente</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {combined.map((item, idx) => (
            <tr key={idx}>
              <td>{item.name}</td>
              <td>{formatCurrency(item.totalPending)}</td>
              <td>
                <Link to={`/accounts-receivable/${item.clientId}`}>
                  Ver Cuenta
                </Link>
              </td>
            </tr>
          ))}

          {combined.length === 0 && (
            <tr>
              <td colSpan="3">No se encontraron clientes.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* =====================
          RESUMEN DE SALDOS
      ===================== */}
      <div className="ar-summary">
        <h2>Resumen de Saldos</h2>

        {!summaryUnlocked ? (
          <div className="ar-lock">
            <div className="ar-lock-icon" aria-hidden="true">
              🔒
            </div>

            <input
              type="text"
              placeholder="Ingrese código"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value);
                if (codeError) setCodeError("");
              }}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              name="ar_unlock_code"
              spellCheck={false}
            />

            <button onClick={handleUnlock}>Desbloquear</button>

            {codeError && <div className="ar-lock-error">{codeError}</div>}
          </div>
        ) : (
          <>
            <p>
              Total de Clientes: <strong>{combined.length}</strong>
            </p>
            <p>
              Total Pendiente:{" "}
              <strong>
                {formatCurrency(
                  combined.reduce(
                    (sum, item) => sum + parseFloat(item.totalPending || 0),
                    0
                  )
                )}
              </strong>
            </p>

            <button className="ar-lock-btn" onClick={handleLock}>
              🔒 Bloquear resumen
            </button>
          </>
        )}
      </div>
      {/* =====================
          FIN RESUMEN
      ===================== */}
    </div>
  );
};

export default AccountsReceivable;
